import { TrendingDown } from "lucide-react";

/**
 * PainLevelCard — Pre-treatment & Post-treatment pain slider.
 *   Renders the 0-10 slider, large readout, and (when both before & after are present)
 *   the point change + % improvement badge.
 *
 * Props:
 *   label: string                         // e.g. "Pain level right now (0-10)"
 *   value: number
 *   onChange: (v: number) => void
 *   accent?: "terracotta" | "sage"
 *   testIdPrefix?: string                 // root id prefix
 *   compareTo?: number | null             // when provided, render delta badge below
 *   compareLabel?: string                 // override "Pain reduction" text
 *   description?: string                  // optional caption above the slider
 */
export default function PainLevelCard({
  label,
  value,
  onChange,
  accent = "terracotta",
  testIdPrefix = "pain",
  compareTo = null,
  compareLabel = null,
  description = null,
}) {
  const accentClass = accent === "sage" ? "accent-sage-500" : "accent-terracotta-500";
  const safeVal = Number.isFinite(value) ? value : 0;
  return (
    <div className="bg-white border border-paper-200 rounded-3xl p-6 sm:p-8" data-testid={`${testIdPrefix}-card`}>
      <div className="label-overline mb-2 text-terracotta-600">{label}</div>
      {description && <p className="text-sm text-ink-700 mb-4">{description}</p>}
      <input
        data-testid={`${testIdPrefix}-slider`}
        type="range"
        min={0}
        max={10}
        value={safeVal}
        onChange={(e) => onChange(parseInt(e.target.value))}
        className={`w-full ${accentClass}`}
      />
      <div className="flex items-center justify-between mt-2">
        <div className="text-xs text-ink-500">0 = no pain</div>
        <div className="font-heading text-4xl text-ink-900" data-testid={`${testIdPrefix}-value`}>{safeVal}</div>
        <div className="text-xs text-ink-500">10 = worst</div>
      </div>
      {compareTo != null && (
        <PainDelta before={compareTo} after={safeVal} testId={`${testIdPrefix}-delta`} contextLabel={compareLabel} />
      )}
    </div>
  );
}

function PainDelta({ before, after, testId, contextLabel = null }) {
  if (before == null || after == null) return null;
  const b = Number(before), a = Number(after);
  const diff = b - a; // positive = improvement (lower today)
  const pos = diff > 0;
  if (contextLabel) {
    // Inter-visit trend badge — points + direction, no % (last baseline may be 0).
    return (
      <div className="text-center mt-4" data-testid={testId}>
        <span
          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
            pos
              ? "bg-sage-500/10 text-sage-700"
              : diff === 0
              ? "bg-paper-200 text-ink-600"
              : "bg-terracotta-500/10 text-terracotta-600"
          }`}
        >
          <TrendingDown className={`w-3.5 h-3.5 ${pos ? "" : "rotate-180"}`} />
          {diff === 0
            ? `Same as last visit (${b}/10)`
            : `${Math.abs(diff)} point${Math.abs(diff) === 1 ? "" : "s"} ${pos ? "lower" : "higher"} ${contextLabel} (was ${b}/10)`}
        </span>
      </div>
    );
  }
  if (b <= 0) return null;
  const pct = Math.round((diff / b) * 100);
  return (
    <div className="text-center mt-4" data-testid={testId}>
      <span
        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
          pos
            ? "bg-sage-500/10 text-sage-700"
            : diff === 0
            ? "bg-paper-200 text-ink-600"
            : "bg-terracotta-500/10 text-terracotta-600"
        }`}
      >
        <TrendingDown className={`w-3.5 h-3.5 ${pos ? "" : "rotate-180"}`} />
        {pos
          ? `${diff} point${diff === 1 ? "" : "s"} ↓ · ${pct}% pain reduction`
          : diff === 0
          ? "No change"
          : `${Math.abs(diff)} point${Math.abs(diff) === 1 ? "" : "s"} ↑ · ${Math.abs(pct)}% increase`}
      </span>
    </div>
  );
}
